import { motion } from "framer-motion";
import { ArrowRight, Play } from "lucide-react";
import { IMAGES, METRICS } from "../lib/constants";

export default function Hero() {
  return (
    <section className="relative pt-32 md:pt-40 pb-20 md:pb-28 overflow-hidden aurora" data-testid="hero-section">
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        {/* Copy column */}
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 mb-6 text-xs uppercase tracking-[0.2em] font-bold text-teal-700"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-teal-600" />
            Brand Lifecycle Management
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.05 }}
            data-testid="hero-headline"
            className="font-display text-[40px] sm:text-[56px] lg:text-[72px] leading-[0.96] font-extrabold text-slate-900"
          >
            India's Mature & Tail-End
            <br />
            <span className="font-editorial italic font-medium text-slate-700">Brand Specialists</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.18 }}
            className="mt-6 max-w-2xl text-base md:text-lg text-slate-600 leading-relaxed"
          >
            Where others see maturity, we see untapped potential. Medstry transforms mature and
            tail-end pharmaceutical brands into profit powerhouses — penetrating extra-urban India through our{" "}
            <span className="text-slate-900 font-semibold">Delta Field Force</span> at{" "}
            <span className="text-slate-900 font-semibold">40–50% lower OPEX</span>.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-8 flex flex-wrap items-center gap-3"
          >
            <a
              href="/#contact"
              data-testid="hero-cta-primary"
              className="inline-flex items-center gap-2 btn-primary px-6 py-3.5 text-sm font-semibold"
            >
              Request Portfolio Analysis <ArrowRight className="h-4 w-4" />
            </a>
            <a
              href="/#approach"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center gap-2 btn-ghost px-6 py-3.5 text-sm font-semibold"
            >
              <Play className="h-4 w-4" /> See How It Works
            </a>
          </motion.div>
        </div>

        {/* Visual column */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, delay: 0.2 }}
          className="lg:col-span-5 relative"
        >
          <div className="relative aspect-[4/5] overflow-hidden bg-slate-100">
            <img
              src={IMAGES.heroBackground}
              alt="Growth curve representing brand lifecycle revitalization"
              className="absolute inset-0 w-full h-full object-cover"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-slate-900/30 via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-md p-5 border border-slate-200">
              <div className="text-[11px] tracking-[0.18em] uppercase font-bold text-teal-700 mb-2">
                Case Snapshot
              </div>
              <div className="font-display text-2xl md:text-3xl font-extrabold text-slate-900 tnum">
                ₹168 Cr
              </div>
              <div className="text-sm text-slate-600 mt-1">
                Annual revenue unlocked from a Top-10 pharma's neglected mature & tail-end portfolio.
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Metrics strip */}
      <div className="max-w-7xl mx-auto px-6 md:px-10 mt-16 md:mt-24" data-testid="hero-metrics">
        <div className="border-y border-slate-200 grid grid-cols-2 md:grid-cols-4 divide-x divide-slate-200">
          {METRICS.map((m) => (
            <div key={m.label} className="px-5 py-7" data-testid={`metric-${m.label.toLowerCase().replace(/\s+/g, '-')}`}>
              <div className="font-display text-3xl md:text-4xl font-extrabold text-slate-900 tnum tracking-tight">
                {m.value}
              </div>
              <div className="text-xs md:text-sm uppercase tracking-[0.14em] text-slate-500 mt-2 font-semibold">
                {m.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
